import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSystemHealthSummary } from '../../../system-health/useSystemHealthSummary';
import type { HealthLevel } from '../../../system-health/types';
import { healthLabel, usageLevel, systemHealthLinkPath } from '../../../system-health/utils';
import { skeletonPulseClass, statusToneClass, viewAllLinkClass, widgetCardClass, widgetTitleClass } from '../../themeHelpers';
import { cn } from '../../../../lib/utils';

export interface SystemHealthOpsSummaryWidgetProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

type ChipTone = 'good' | 'warn' | 'bad' | 'neutral';

const QuickChip: React.FC<{ label: string; value: string; tone?: ChipTone }> = ({
  label,
  value,
  tone = 'neutral',
}) => {
  const { colors } = useTheme();
  const style = statusToneClass(colors, tone);

  return (
    <div className={cn('min-w-[100px] flex-1 rounded-xl border px-3 py-2', style.shell)}>
      <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>{label}</p>
      <p className={cn('mt-0.5 truncate text-sm font-semibold', style.value || undefined)}>{value}</p>
    </div>
  );
};

const toneFromLevel = (level: HealthLevel): ChipTone => {
  switch (level) {
    case 'ok':
      return 'good';
    case 'warning':
      return 'warn';
    case 'error':
      return 'bad';
    case 'paused':
    case 'info':
    default:
      return 'neutral';
  }
};

const SystemHealthOpsSummaryWidget: React.FC<SystemHealthOpsSummaryWidgetProps> = ({ embedded }) => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const healthLink = systemHealthLinkPath(can('system_health', 'edit'));
  const { data, isLoading, error } = useSystemHealthSummary();
  const cardClass = widgetCardClass(colors, embedded);

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`${widgetTitleClass(colors)} mb-3`}>Ops summary</h2>
        <div className={`animate-pulse h-24 rounded-lg ${skeletonPulseClass(colors)}`} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cardClass}>
        <h2 className={widgetTitleClass(colors)}>Ops summary</h2>
        <p className={`text-sm mt-3 ${colors.textMuted}`}>Unable to load system health data.</p>
      </div>
    );
  }

  const diskTone = toneFromLevel(usageLevel(data.disk.usedPercent));
  const memTone = toneFromLevel(usageLevel(data.memory.usedPercent));
  const syncTone =
    data.emailSync.error > 0 ? 'bad' : data.emailSync.warning > 0 ? 'warn' : data.emailSync.total > 0 ? 'good' : 'neutral';
  const dbTone = data.database.connected ? 'good' : 'bad';

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2">
        <h2 className={widgetTitleClass(colors)}>Ops summary</h2>
        <Link to={healthLink} className={viewAllLinkClass(colors)}>
          System Health
        </Link>
      </div>
      <div className="flex flex-wrap gap-2">
        <QuickChip label="Overall" value={healthLabel(data.overallStatus)} tone={toneFromLevel(data.overallStatus)} />
        <QuickChip label="Disk" value={`${data.disk.usedPercent}% used`} tone={diskTone} />
        <QuickChip label="Memory" value={`${data.memory.usedPercent}% used`} tone={memTone} />
        <QuickChip
          label="Email sync"
          value={
            data.emailSync.total === 0
              ? 'No accounts'
              : `${data.emailSync.healthy}/${data.emailSync.total} OK`
          }
          tone={syncTone}
        />
        <QuickChip label="Database" value={data.database.connected ? 'Connected' : 'Down'} tone={dbTone} />
        <QuickChip label="Online" value={`${data.onlineUsers.uniqueUsers} users`} tone="neutral" />
        <QuickChip
          label="Failed logins"
          value={String(data.security.failedLogins24h)}
          tone={data.security.failedLogins24h >= 10 ? 'warn' : 'neutral'}
        />
      </div>
    </div>
  );
};

export default SystemHealthOpsSummaryWidget;
